/**
 * Sidebar — Maasai Red collapsible dashboard rail (V22 Directive §1.A).
 *
 *   • Wide mode:   w-[250px] with icon + label + tier badge in the footer.
 *   • Rail mode:   w-[64px] icons only; label revealed via Radix Tooltip.
 *
 * Visual Design:
 *   • Surface:  Maasai Red #922529 (solid, no gradients)
 *   • Primary labels/icons: White #FFFFFF (7:1 contrast)
 *   • Secondary labels: Light Slate #F1F5F9 (4.5:1 contrast)
 *   • Active items: border-l-4 border-amber-400 + white/10% overlay
 *   • Focus rings: 2px amber-400
 *   • Touch targets: 44×44px minimum
 *
 * Reversed Brand Logo (top):
 *   • White box containing Maasai Red monogram + white logotype text
 *   • Collapsed: white box with Maasai Red monogram only
 *
 * Toggling the collapsed state dispatches a global `resize` event so the
 * DashboardGrid (and any recharts inside) recompute their column count.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShieldCheck, Lock, LogOut } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppCopy } from "@/lib/app-i18n";
import { getNavGroupsForUser, getTierLabel } from "@/lib/dashboard-nav";
import { useOnboardingGate, GATE_ALLOWED_PATHS } from "@/context/OnboardingGateContext";
import AdminModuleNavGroup from "@/components/admin/AdminModuleNavGroup";
import SortableNavGroup from "@/components/admin/SortableNavGroup";

/* ── Maasai Red palette tokens (exported for AdminModuleNavGroup) ── */
export const MR = {
    BG:          "#922529",
    TEXT_PRIMARY:  "#FFFFFF",
    TEXT_SECONDARY: "#F1F5F9",
    ACTIVE_BG:   "rgba(255,255,255,0.12)",
    HOVER_BG:    "rgba(255,255,255,0.08)",
    DIVIDER:     "rgba(255,255,255,0.15)",
    ACTIVE_BORDER: "#FBBF24",   // amber-400
    FOCUS_RING:    "#FBBF24",
};

const STORAGE_KEY = "md_sidebar_collapsed";

/* ── Persist helpers ─────────────────────────────────────────────── */
function readInitialCollapsed() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

/* ── Hook: exposes { collapsed, toggle } and mirrors on <html>. ──── */
export function useSidebarCollapsed() {
    const [collapsed, setCollapsed] = useState(readInitialCollapsed);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
        } catch {
            /* ignore */
        }
        document.documentElement.setAttribute(
            "data-sidebar",
            collapsed ? "collapsed" : "expanded",
        );
        window.dispatchEvent(new Event("resize"));
    }, [collapsed]);

    const toggle = useCallback(() => setCollapsed((c) => !c), []);
    return { collapsed, setCollapsed, toggle };
}

/* ── Rail width tokens ──────────────────────────────────────────── */
export const SIDEBAR_WIDTH_EXPANDED = 250;
export const SIDEBAR_WIDTH_COLLAPSED = 64;

/* ── Reversed Brand Logo ─────────────────────────────────────────── */
function ReversedBrandLogo({ collapsed }) {
    return (
        <span className="flex items-center gap-2 min-w-0">
            {/* White box containing Maasai Red monogram */}
            <span
                data-testid="reversed-monogram"
                className="inline-flex items-center justify-center rounded-lg font-heading font-bold tracking-wider shadow-sm shrink-0"
                style={{
                    background: "#FFFFFF",
                    color: MR.BG,
                    width: collapsed ? 32 : 28,
                    height: collapsed ? 32 : 28,
                    fontSize: collapsed ? 12 : 10,
                    letterSpacing: "0.05em",
                }}
                aria-label="Melian Dialogue"
            >
                MD
            </span>
            {/* White logotype text */}
            {!collapsed && (
                <span
                    data-testid="reversed-logotype"
                    className="font-heading font-extrabold tracking-tight leading-none truncate"
                    style={{ color: MR.TEXT_PRIMARY, fontSize: "0.9375rem" }}
                >
                    Melian Dialogue<span style={{ color: MR.ACTIVE_BORDER }}>.</span>
                </span>
            )}
        </span>
    );
}

/* ── Group header ────────────────────────────────────────────────── */
function GroupTitle({ collapsed, children, titleTo, testId, onNavigate }) {
    if (collapsed) {
        return <div className="my-2 mx-3 h-px" style={{ background: MR.DIVIDER }} aria-hidden />;
    }
    if (titleTo) {
        return (
            <NavLink
                to={titleTo}
                end
                onClick={onNavigate}
                data-testid={testId}
                className="flex items-center gap-2 px-3 py-2 mb-1 rounded-md text-caption uppercase tracking-[0.18em] font-bold transition-colors"
                style={({ isActive }) => ({
                    color: isActive ? MR.TEXT_PRIMARY : MR.TEXT_SECONDARY,
                    background: isActive ? MR.ACTIVE_BG : "transparent",
                    borderLeft: isActive ? `3px solid ${MR.ACTIVE_BORDER}` : "3px solid transparent",
                })}
            >
                <ShieldCheck className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
                <span className="truncate">{children}</span>
            </NavLink>
        );
    }
    return (
        <div
            className="px-3 text-caption uppercase tracking-[0.18em] font-semibold mb-1.5"
            style={{ color: MR.TEXT_SECONDARY, opacity: 0.85 }}
        >
            {children}
        </div>
    );
}

/* ── Single row (with tooltip when collapsed) ────────────────────── */
function NavRow({ link, collapsed, onNavigate, t }) {
    const Icon = link.icon;
    const label = link.labelKey ? t(link.labelKey, link.label || link.labelKey) : link.label;
    const { stage } = useOnboardingGate();
    // Progressive onboarding lock (2026-09-05): while a stage is active,
    // every nav link is greyed out and unclickable except the ones the
    // active stage still allows (always the dashboard home; Settings too
    // during the profile-completion stage).
    const locked = stage !== "complete" && !(GATE_ALLOWED_PATHS[stage] || []).includes(link.to);

    if (locked) {
        const row = (
            <div
                aria-disabled="true"
                data-testid={`${link.testId}-locked`}
                className="outline-none opacity-40 cursor-not-allowed"
                style={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "0.375rem",
                    fontWeight: 500,
                    minHeight: "44px",
                    ...(collapsed
                        ? { justifyContent: "center", width: 44, height: 44, margin: "0 auto", gap: "3px" }
                        : { gap: "0.625rem", padding: "0.5rem 0.75rem" }),
                    color: MR.TEXT_SECONDARY,
                }}
            >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                {!collapsed && <span className="truncate text-sm flex-1">{label}</span>}
                <Lock className="w-3 h-3 shrink-0" strokeWidth={2} />
            </div>
        );
        if (!collapsed) return row;
        return (
            <Tooltip>
                <TooltipTrigger asChild>{row}</TooltipTrigger>
                <TooltipContent
                    side="right"
                    className="text-caption font-heading border"
                    style={{ background: "#0F172A", color: "#F8FAFC", borderColor: "rgba(255,255,255,0.12)" }}
                >
                    {label} — complete the current step first
                </TooltipContent>
            </Tooltip>
        );
    }

    const row = (
        <NavLink
            to={link.to}
            end={link.end}
            onClick={onNavigate}
            data-testid={link.testId}
            className="outline-none block rounded-md focus-visible:ring-2"
            style={{ "--tw-ring-color": MR.FOCUS_RING }}
        >
            {({ isActive }) => {
                // Sept 2026 fix: styling lives on this INNER div, not on the
                // NavLink's own props. Radix's <TooltipTrigger asChild> clones
                // whichever element it wraps directly — when that element's
                // `style` is still an *unresolved function* (as NavLink's is,
                // before NavLink itself renders), Slot's prop-merge silently
                // collapses it to `{}`, so every collapsed-rail icon lost its
                // colour AND its 44×44 sizing. Resolving isActive here, inside
                // NavLink's own render, and applying the object to a child
                // Slot never touches, sidesteps that entirely.
                const base = {
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "0.375rem",
                    fontWeight: 500,
                    transition: "background 150ms, color 150ms",
                    minHeight: "44px",
                };
                const style = collapsed
                    ? {
                        ...base,
                        justifyContent: "center",
                        width: 44,
                        height: 44,
                        margin: "0 auto",
                        color: isActive ? MR.TEXT_PRIMARY : MR.TEXT_SECONDARY,
                        background: isActive ? MR.ACTIVE_BG : "transparent",
                        borderLeft: isActive ? `4px solid ${MR.ACTIVE_BORDER}` : "4px solid transparent",
                    }
                    : {
                        ...base,
                        gap: "0.625rem",
                        color: isActive ? MR.TEXT_PRIMARY : link.primary ? MR.TEXT_PRIMARY : MR.TEXT_SECONDARY,
                        background: isActive ? MR.ACTIVE_BG : "transparent",
                        borderLeft: isActive ? `4px solid ${MR.ACTIVE_BORDER}` : "4px solid transparent",
                        padding: isActive ? "0.5rem 0.75rem 0.5rem 8px" : "0.5rem 0.75rem 0.5rem 12px",
                    };
                return (
                    <div
                        style={style}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = MR.HOVER_BG; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? MR.ACTIVE_BG : "transparent"; }}
                    >
                        <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                        {!collapsed && <span className="truncate text-sm">{label}</span>}
                    </div>
                );
            }}
        </NavLink>
    );

    if (!collapsed) return row;
    return (
        <Tooltip>
            <TooltipTrigger asChild>{row}</TooltipTrigger>
            <TooltipContent
                side="right"
                className="text-caption font-heading border"
                style={{ background: "#0F172A", color: "#F8FAFC", borderColor: "rgba(255,255,255,0.12)" }}
            >
                {label}
            </TooltipContent>
        </Tooltip>
    );
}

/* ── Logout row — last item in the nav, styled like a NavRow ────── */
function LogoutRow({ collapsed, onLogout, t }) {
    const label = t("header.logout", "Log out");
    const row = (
        <button
            type="button"
            onClick={onLogout}
            data-testid="sidebar-logout-btn"
            className="outline-none w-full rounded-md"
            style={{
                display: "flex",
                alignItems: "center",
                borderRadius: "0.375rem",
                fontWeight: 500,
                transition: "background 150ms, color 150ms",
                minHeight: "44px",
                color: MR.TEXT_SECONDARY,
                background: "transparent",
                ...(collapsed
                    ? { justifyContent: "center", width: 44, height: 44, margin: "0 auto" }
                    : { gap: "0.625rem", padding: "0.5rem 0.75rem 0.5rem 12px" }),
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = MR.HOVER_BG; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
            <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.75} />
            {!collapsed && <span className="truncate text-sm">{label}</span>}
        </button>
    );
    if (!collapsed) return row;
    return (
        <Tooltip>
            <TooltipTrigger asChild>{row}</TooltipTrigger>
            <TooltipContent
                side="right"
                className="text-caption font-heading border"
                style={{ background: "#0F172A", color: "#F8FAFC", borderColor: "rgba(255,255,255,0.12)" }}
            >
                {label}
            </TooltipContent>
        </Tooltip>
    );
}

/* ── Main component ──────────────────────────────────────────────── */
export default function Sidebar({
    user,
    collapsed = false,
    onToggle,
    onNavigate,
    onLogout,
    className = "",
    showToggle = true,
}) {
    const { t, isRtl } = useAppCopy();
    const groups = getNavGroupsForUser(user);
    const tierText = getTierLabel(user, t);
    const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

    return (
        <TooltipProvider delayDuration={80}>
            <div
                className={`flex flex-col h-full transition-[width] duration-300 ease-in-out ${className}`}
                style={{
                    width,
                    minWidth: width,
                    background: MR.BG,
                    borderRight: "none",
                }}
                dir={isRtl ? "rtl" : undefined}
                data-testid="dashboard-sidebar"
                data-collapsed={collapsed ? "true" : "false"}
            >
                {/* Logo + collapse control */}
                <div
                    className={`h-14 flex items-center shrink-0 ${collapsed ? "justify-center px-2" : "px-4 justify-between gap-2"}`}
                    style={{ borderBottom: `1px solid ${MR.DIVIDER}` }}
                >
                    <Link
                        to="/app"
                        onClick={onNavigate}
                        className="flex items-center gap-2 min-w-0"
                        data-testid="sidebar-logo"
                    >
                        <ReversedBrandLogo collapsed={collapsed} />
                    </Link>
                    {showToggle && !collapsed && (
                        <button
                            type="button"
                            onClick={onToggle}
                            data-testid="sidebar-collapse-btn"
                            aria-label="Collapse sidebar"
                            className="p-1 rounded-md transition-colors"
                            style={{ color: MR.TEXT_SECONDARY }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = MR.HOVER_BG; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                            <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                    )}
                </div>

                {/* Expand button when in rail mode */}
                {showToggle && collapsed && (
                    <button
                        type="button"
                        onClick={onToggle}
                        data-testid="sidebar-expand-btn"
                        aria-label="Expand sidebar"
                        className="mx-auto my-2 p-1 rounded-md transition-colors"
                        style={{ color: MR.TEXT_SECONDARY }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = MR.HOVER_BG; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                        <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                )}

                {/* Nav groups */}
                <nav className={`flex-1 overflow-y-auto ${collapsed ? "py-3 px-1" : "py-3 px-2"} space-y-3`}>
                    {groups.map((group) => (
                        <div key={group.key}>
                            <GroupTitle
                                collapsed={collapsed}
                                titleTo={group.titleTo}
                                testId={group.titleTestId}
                                onNavigate={onNavigate}
                            >
                                {t(group.titleKey, group.titleFallback)}
                            </GroupTitle>
                            <div className="space-y-1.5">
                                {group.key === "admin_tools" ? (
                                    <SortableNavGroup
                                        storageKey="md_admin_tools_order"
                                        items={group.links.map((link) => ({
                                            id: link.to,
                                            to: link.to,
                                            label: link.labelKey ? t(link.labelKey, link.label || link.labelKey) : link.label,
                                            icon: link.icon,
                                            testId: link.testId,
                                        }))}
                                        collapsed={collapsed}
                                        onNavigate={onNavigate}
                                    />
                                ) : (
                                    group.links.map((link) => (
                                        <NavRow
                                            key={link.to}
                                            link={link}
                                            collapsed={collapsed}
                                            onNavigate={onNavigate}
                                            t={t}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    ))}
                    {user?.role === "admin" && (
                        <AdminModuleNavGroup collapsed={collapsed} onNavigate={onNavigate} />
                    )}
                    {onLogout && (
                        <div className={collapsed ? "pt-1" : "pt-1 space-y-1.5"} style={{ borderTop: `1px solid ${MR.DIVIDER}` }}>
                            {!collapsed && <div className="h-2" aria-hidden />}
                            <LogoutRow collapsed={collapsed} onLogout={onLogout} t={t} />
                        </div>
                    )}
                </nav>

                {/* User footer */}
                <div className={`shrink-0 ${collapsed ? "p-2" : "p-3"}`} style={{ borderTop: `1px solid ${MR.DIVIDER}` }}>
                    {collapsed ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div
                                    className="w-8 h-8 mx-auto rounded-full flex items-center justify-center text-caption font-bold cursor-default"
                                    style={{ background: MR.ACTIVE_BG, color: MR.TEXT_PRIMARY }}
                                    data-testid="sidebar-tier-badge"
                                >
                                    {(user?.full_name?.[0] || user?.email?.[0] || "?").toUpperCase()}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent
                                side="right"
                                style={{ background: "#0F172A", color: "#F8FAFC", border: "1px solid rgba(255,255,255,0.12)" }}
                            >
                                <div className="font-heading font-semibold text-ui-med" style={{ color: "#F8FAFC" }}>
                                    {user?.full_name?.split(" ")[0] || "User"}
                                </div>
                                <div className="text-caption uppercase tracking-wider" style={{ color: MR.ACTIVE_BORDER }}>
                                    {tierText}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <div className="flex items-center gap-2 min-w-0">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-caption font-bold shrink-0"
                                style={{ background: MR.ACTIVE_BG, color: MR.TEXT_PRIMARY }}
                            >
                                {(user?.full_name?.[0] || user?.email?.[0] || "?").toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold truncate leading-tight" style={{ color: MR.TEXT_PRIMARY }}>
                                    <bdi>{user?.full_name?.split(" ")[0] || "User"}</bdi>
                                </div>
                                <div
                                    className="text-caption uppercase tracking-[0.15em] font-semibold truncate"
                                    style={{ color: MR.ACTIVE_BORDER }}
                                    data-testid="sidebar-tier-badge"
                                >
                                    {tierText}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </TooltipProvider>
    );
}
